// Unit and building definitions. In the real game these come from a versioned
// server config; the sim only ever sees a GameConfig object.

import { MAP_SIZE, TILE, TICKS_PER_SECOND } from './fixed.ts';

export type TargetPreference = 'any' | 'defence' | 'resource';

/** What a unit moves through. Ground is the default. */
export type Domain = 'ground' | 'aircraft' | 'drone' | 'missile' | 'sea';

/**
 * What kind of threat a unit is, and so which defences can engage it. Real air
 * defence is layered this way: a C-RAM gun handles rockets and drones, Iron Dome
 * rockets and cruise missiles, Patriot and David's Sling ballistic missiles,
 * THAAD and Arrow 3 the high, fast ones (ICBMs, and glide vehicles at best).
 */
export const THREATS = ['infantry', 'vehicle', 'aircraft', 'drone', 'ship', 'submarine', 'rocket', 'cruise', 'ballistic', 'icbm', 'hgv'] as const;
export type Threat = typeof THREATS[number];

/** Display names for threat classes, for the target-priority chips. */
export const THREAT_NAMES: Record<Threat, string> = {
  infantry: 'Infantry', vehicle: 'Vehicles', aircraft: 'Aircraft', drone: 'Drones', ship: 'Ships', submarine: 'Submarines',
  rocket: 'Rockets', cruise: 'Cruise missiles', ballistic: 'Ballistic missiles', icbm: 'ICBMs', hgv: 'Glide vehicles',
};

export const RESOURCES = ['fuel', 'steel', 'supplies'] as const;
export type Resource = typeof RESOURCES[number];
export type Cost = Partial<Record<Resource, number>>;

export interface UnitDef {
  name: string;
  /** The real system it is modelled on, shown under the name. */
  real?: string;
  /** One line on what it does on the field, shown in the console. */
  role?: string;
  /** What defences see it as. Derived from domain and infantry when absent (see threatOf). */
  threat?: Threat;
  /** Rounds per trigger pull, `burstGap` ticks apart; `attackTicks` is then the reload after the burst. */
  burst?: number;
  burstGap?: number;
  /** Damage multiplier against vehicles: anti-tank weapons. */
  antiArmor?: number;
  /** Threats its weapon cannot harm, so it never fires at them: a sniper's rifle against armour. */
  cannotHit?: Threat[];
  /** A marksman: shoots the crews of manned defences (see BuildingDef.manned) from `range`, and only plants a charge once none is left. */
  sniper?: boolean;
  /** An explosive weapon: its blast reaches this far (units) round the point of impact, half damage at the edge. */
  splash?: number;
  /** A fast jet: it never hovers, but makes passes, releasing on the way in and flying on through before coming round. */
  jet?: boolean;
  /** A jet's cannon, strafing whatever it is running in on: `damage` a burst every `everyTicks`, out to `range` units. */
  gun?: { damage: number; everyTicks: number; range: number };
  /** Chance, 0 to 1, that it shakes off a missile or a burst fired at it by jinking hard (and, for a jet, dropping flares). */
  evasion?: number;
  /** Recon: unarmed, it reveals hidden mines and marks defences within `range` (see sim.ts). */
  recon?: boolean;
  /** One-way: it dies with its first hit, like a loitering munition or a missile. */
  oneWay?: boolean;
  /**
   * A missile that comes down on a tile: deployed by tile like a strike, it hangs
   * in its terminal dive for `fuseTicks` (when interceptors can reach it) and
   * then hits everything in `radius`, falling from `damage` to `edgeDamage`.
   */
  warhead?: { fuseTicks: number; radius: number; damage: number; edgeDamage: number; cooldownTicks: number };
  /** A strike carried in by this missile unit, which defences can shoot down: a nuke on an ICBM. */
  deliveredBy?: string;
  /** Launched from this unit, which must be on the field: the missile flies from it to the target tile. */
  launchedFrom?: string;
  /** A landing craft: once it has been on the water `afterTicks`, it puts these troops ashore beside it. */
  carries?: { units: Record<string, number>; afterTicks: number };
  hp: number;
  damage: number;       // per hit
  attackTicks: number;  // ticks between hits
  range: number;        // units, measured to the target's edge
  speed: number;        // units per tick
  housing: number;
  prefers: TargetPreference;
  /** Defaults to ground. Non-ground units fly straight at their target and ignore fences and mines. */
  domain?: Domain;
  /** Training cost. Absent means the unit cannot be trained (free units). */
  cost?: Cost;
  /** The campaign chapter from which the waves may send it. Defaults to 1. The player can train every unit from the start. */
  tier?: number;
  /** Foot soldier: cannot shoot structures, only plant an explosive on them (see sim.ts). */
  infantry?: boolean;
  /** Chance, 0 to 1, that this unit's own interceptors shoot down a missile fired at it. */
  interceptChance?: number;
  /** A ship that launches this unit (a cruise missile) from where it sits, every so many ticks, once a target is within `range` (units). */
  launches?: { unit: string; everyTicks: number; range?: number };
  /** Deployed as a support strike instead of a unit: it lands once and is gone. */
  strike?: StrikeDef;
  /** Hit by an EMP: a disabled unit neither moves nor fires. */
  electronic?: boolean;
  /** Building type that must stand in the village to train it, e.g. a Tank Factory for tanks. */
  producer?: string;
  /** Tech that must be researched first. */
  tech?: string;
}

export interface StrikeDef {
  kind: 'nuke' | 'emp';
  radius: number;       // units, from the landing point
  damage?: number;      // nukes: damage to a building at ground zero
  edgeDamage?: number;  // nukes: damage at the blast's edge; it falls off in a straight line from ground zero
  disableTicks?: number; // EMPs: how long electronics stay down
  cooldownTicks: number; // per raid, per strike type, after landing
}

/** The defender's abilities in a campaign wave, called in by the player on a cooldown. */
export type AbilityKind = 'artillery' | 'smoke' | 'repair';
export const ABILITY_KINDS: readonly AbilityKind[] = ['artillery', 'smoke', 'repair'];
export interface AbilityDef {
  name: string;
  /** The real thing it is modelled on. */
  real?: string;
  /** What it does, in a line. */
  role: string;
  /** Aimed at a tile, or at one of the defender's own buildings. */
  aim: 'tile' | 'building';
  /** Ticks before it can be called again, within a wave. */
  cooldownTicks: number;
  /** Artillery and smoke: how far from the aim point it reaches, in units. */
  radius?: number;
  /** Artillery: damage to an attacker at the aim point, half at the edge. */
  damage?: number;
  /** Artillery: ticks from the call to the shells landing. */
  countdownTicks?: number;
  /** Smoke: how long the cloud lasts. */
  lastsTicks?: number;
  /** Repair: the share of a building's full hit points put back. */
  repairPct?: number;
}

export interface BuildingDef {
  name: string;
  hp: number;
  size: number;         // footprint in tiles (square)
  kind: 'hq' | 'defence' | 'resource' | 'storage' | 'army' | 'wall' | 'production' | 'research';
  damage?: number;
  attackTicks?: number;
  range?: number;       // units, from building center
  minRange?: number;
  /** The real system it is modelled on, shown under the name. */
  real?: string;
  /** Which threats this defence can engage. Defaults to infantry and vehicles. */
  targets?: Threat[];
  /** Rounds per trigger pull, `burstGap` ticks apart; `attackTicks` is then the reload after the burst. */
  burst?: number;
  burstGap?: number;
  /** Its rounds are missiles that fly, locked on, at this speed (units per tick), rather than hitting at once. */
  projectile?: number;
  /** Interceptors it will have in the air at one threat at most before it looks for another. */
  salvo?: number;
  /** The order it engages threats in until the player sets their own; anything not listed comes after, nearest first. */
  defaultPriority?: Threat[];
  /** An explosive round or warhead: its blast reaches this far (units) round the target, half damage at the edge. */
  splash?: number;
  /** A bunker's squad: this many fire from it at full strength, fewer as it is battered (one per share of its health). */
  garrison?: number;
  /** Its crew fights in the open (a sandbagged gun, a tower, a missile team): a marksman can pick them off. */
  manned?: boolean;
  /** A gun that ceases fire the moment its target is down and slews to the next after `attackTicks`, rather than finishing the burst. */
  ceaseOnKill?: boolean;
  /** Concrete: small arms do half damage; anti-tank weapons do their full anti-armour damage; charges and shells do full. */
  fortified?: boolean;
  /** Electric fence: a wall that shocks ground units next to it, `damage` every `attackTicks`. */
  shock?: boolean;
  /** Mine: hidden from enemy targeting, detonates once on a unit it targets within `range`, hitting every such unit within `splash`. */
  mine?: { splash: number };
  // ---- economy (all scale with level, see economy.ts) ----
  /** Cost of level 1. Upgrading to level L costs this times L squared. */
  cost?: Cost;
  /** Collector output per hour at level 1. */
  produces?: { resource: Resource; perHour: number };
  /** Storage added at level 1. */
  stores?: Cost;
  /** Army housing added at level 1. */
  housing?: number;
  /** Max count allowed, indexed by HQ level - 1. Absent means unlimited. */
  maxCount?: number[];
  /** Hit by an EMP: a disabled defence does not fire. */
  electronic?: boolean;
  /** Placed on the sea along the west edge instead of inside the plan. */
  sea?: boolean;
  /** Shots this defence can fire in one raid before it runs dry. Absent means unlimited. */
  ammo?: number;
  /** Chance, 0 to 1, that each interceptor kills the missile it was fired at, overall or per threat class. Rolled by the raid's seeded RNG. */
  interceptChance?: number | Partial<Record<Threat, number>>;
  /** Its shots are missiles, so a target with interceptors of its own may shoot them down. */
  missiles?: boolean;
  /** A station that keeps one defender unit roaming within `radius` units of it; `fromSea`: it starts off a carrier offshore. */
  patrol?: { unit: string; radius: number; fromSea?: boolean };
  /** Building that must stand in the village before this can be built, e.g. a Shipyard for a Fishing Boat. */
  producer?: string;
  /** Collector that only runs when enough water supply covers it. */
  needsWater?: boolean;
  /** Farms a water supply can cover, per level. */
  waters?: number;
  /** Tech that must be researched before it can be built. */
  tech?: string;
}

/** A research item, bought with resources at a Research Lab. Researching is instant. */
export interface TechDef {
  name: string;
  cost: Cost;
  /** Tech that must be researched first. Forms the tree. */
  requires?: string;
}

export interface GameConfig {
  version: number;
  units: Record<string, UnitDef>;
  buildings: Record<string, BuildingDef>;
  raidTicks: number;    // max raid length
  /** Free units every attacker gets on every raid, on top of their trained army. */
  freeArmy: Record<string, number>;
  techs: Record<string, TechDef>;
  abilities: Record<AbilityKind, AbilityDef>;
}

const tilesPerSecond = (t: number) => Math.floor((t * TILE) / TICKS_PER_SECOND);

/** What defences see a unit as. */
export function threatOf(def: UnitDef): Threat {
  if (def.threat) return def.threat;
  if (def.infantry) return 'infantry';
  switch (def.domain) {
    case 'sea': return 'ship';
    case 'aircraft': return 'aircraft';
    case 'drone': return 'drone';
    case 'missile': return 'cruise';
    default: return 'vehicle';
  }
}

/** A defence's chance to kill a threat with one interceptor; 1 for a defence that does not roll. */
export function interceptChanceVs(def: BuildingDef, threat: Threat): number | undefined {
  const c = def.interceptChance;
  if (c === undefined || typeof c === 'number') return c;
  return c[threat] ?? 0;
}

/** Hit points at a level: +20 percent of base per level above 1. Integer. */
export function hpForLevel(def: BuildingDef, level: number): number {
  return Math.floor((def.hp * (10 + 2 * (level - 1))) / 10);
}

/** A strike's damage to something `d` units from where it lands: full at ground zero, falling to its edge damage at its radius. Integer. */
export function strikeDamage(s: StrikeDef, d: number): number {
  const full = s.damage ?? 0, edge = s.edgeDamage ?? full;
  if (d <= 0) return full;
  return full - Math.floor(((full - edge) * Math.min(d, s.radius)) / s.radius);
}

export const DEFAULT_CONFIG: GameConfig = {
  version: 22,
  raidTicks: 180 * TICKS_PER_SECOND,
  freeArmy: { conscript: 10 },
  // ---- the defender's abilities in a campaign wave: a wave is played, not watched ----
  abilities: {
    // a battery of 155 mm guns somewhere behind the village: a fire mission on a tile, shells down three seconds later
    artillery: {
      name: 'Artillery strike', real: 'M777 battery, fire for effect', role: 'Shells on a tile three seconds after the call: hurts everything on the ground there, your own buildings spared',
      aim: 'tile', radius: TILE * 3, damage: 90, countdownTicks: 3 * TICKS_PER_SECOND, cooldownTicks: 45 * TICKS_PER_SECOND,
    },
    // smoke rounds: troops and drones in the cloud cannot see to shoot or to pick a target; your guns still see them
    smoke: {
      name: 'Smoke screen', real: 'M825 white phosphorus smoke', role: 'A cloud on a tile for eight seconds: troops and drones inside it cannot shoot or pick a target; your guns can',
      aim: 'tile', radius: TILE * 4, lastsTicks: 8 * TICKS_PER_SECOND, cooldownTicks: 40 * TICKS_PER_SECOND,
    },
    // an engineer crew with the sandbags and the welding kit: one building patched up on the spot
    repair: {
      name: 'Emergency repair', real: 'Combat engineers', role: 'Puts 40 percent of a building\'s hit points back, on the spot; not on rubble',
      aim: 'building', repairPct: 40, cooldownTicks: 60 * TICKS_PER_SECOND,
    },
  },
  techs: {
    armor: { name: 'Armour Plating', cost: { fuel: 300, steel: 200 } },
    drones: { name: 'Drone Telemetry', cost: { steel: 250, supplies: 150 } },
    flight: { name: 'Flight Training', cost: { fuel: 400, supplies: 200 }, requires: 'drones' },
  },
  // Every unit is modelled on one real system, named in `real`, and fills a role no other unit does:
  // `role` is the one line the console shows under its name. Rates of fire are the real ones,
  // compressed where a raid's few minutes demand it, with damage per round scaled so the fight stays
  // balanced: a burst of small hits rather than one big one. A man is 45 hp in body armour and 30
  // without; a two-man team is 80. Infantry cannot shoot structures: they plant a charge at arm's
  // length (see sim.ts), so a foot unit's range is the reach of its gun against patrols and crews.
  units: {
    rifleman: {
      name: 'Rifleman',
      real: 'AK-74M assault rifle',
      role: 'Line infantry, cheap and quick: runs up to a building and plants a charge, firing on patrols it meets on the way.',
      infantry: true,
      hp: 45,
      // three-round bursts, about the aimed rate of a rifleman in contact
      damage: 3,
      burst: 3,
      burstGap: 2,
      attackTicks: 20,
      range: Math.floor(TILE * 0.6),
      speed: tilesPerSecond(2),
      housing: 1,
      prefers: 'any',
      cost: { supplies: 10 },
      producer: 'barracks',
    },
    marksman: {
      name: 'Marksman',
      real: 'SVD Dragunov',
      role: 'Picks off the crews of nests and posts in the open from outside their walls; no use against armour, aircraft or a hull.',
      infantry: true,
      // a 7.62 mm rifle: deadly to men, useless against armour, aircraft or a ship's hull
      cannotHit: ['vehicle', 'aircraft', 'ship'],
      // shoots the crew of any manned defence (see BuildingDef.manned); he plants a charge only once none is left
      sniper: true,
      hp: 40,
      // a designated marksman's aimed shot every second and a half
      damage: 10,
      attackTicks: 30,
      range: Math.floor(TILE * 3.5),
      speed: tilesPerSecond(2),
      housing: 1,
      prefers: 'any',
      cost: { supplies: 15 },
      tier: 2,
      producer: 'barracks',
    },
    heavy_gunner: {
      name: 'Machine Gun Team',
      real: 'PKM machine gun (gunner and No. 2)',
      role: 'A two-man belt-fed gun: long bursts that outrange a rifle, and it goes for the guns first.',
      infantry: true,
      // two men: the gunner and the number two carrying the belts
      hp: 80,
      // long bursts of eight from a belt-fed gun
      damage: 3,
      burst: 8,
      burstGap: 2,
      attackTicks: 20,
      range: Math.floor(TILE * 1.2),
      speed: tilesPerSecond(1.6),
      housing: 2,
      prefers: 'defence',
      cost: { supplies: 30 },
      tier: 3,
      producer: 'barracks',
    },
    at_team: {
      name: 'Anti-Tank Team',
      real: '9M133 Kornet ATGM team',
      role: 'A two-man guided-missile team: one heavy missile every eight seconds from four tiles out, twice as hard on armour.',
      // on foot, so mines and guns that hunt infantry see it as infantry, but it fires its missiles at buildings and armour alike
      threat: 'infantry',
      hp: 80,
      // a guided missile every eight seconds: reloading a Kornet launcher takes about that long
      damage: 120,
      attackTicks: 8 * TICKS_PER_SECOND,
      antiArmor: 2,
      splash: Math.floor(TILE * 0.5),
      range: TILE * 4,
      speed: tilesPerSecond(1.8),
      housing: 2,
      prefers: 'defence',
      cost: { supplies: 40, steel: 20 },
      tier: 2,
      producer: 'barracks',
    },
    tank: {
      name: 'Main Battle Tank',
      real: 'T-90M (125 mm 2A46M-5, autoloader)',
      role: 'Armour: shrugs off small arms and shells buildings from two and a half tiles, each round blasting what stands beside its target.',
      hp: 400,
      // the autoloader manages about eight rounds a minute: one heavy round every seven seconds
      damage: 140,
      // 125 mm HE-FRAG: the blast reaches nearly a tile round the strike
      splash: Math.floor(TILE * 0.8),
      attackTicks: 7 * TICKS_PER_SECOND,
      range: Math.floor(TILE * 2.5),
      speed: tilesPerSecond(1),
      housing: 6,
      prefers: 'defence',
      cost: { fuel: 200 },
      tier: 2,
      producer: 'tank_factory',
      tech: 'armor',
    },
    scout_drone: {
      name: 'Recon UAV',
      real: 'Orlan-10',
      role: 'Unarmed eyes: finds hidden mines so the army walks round them, and marks the defences it watches so they take more damage.',
      // unarmed: it finds hidden mines so the army walks around them, and marks the defences it watches for the guns
      recon: true,
      hp: 20,
      damage: 0,
      attackTicks: 1,
      range: TILE * 3,
      speed: tilesPerSecond(3),
      housing: 1,
      prefers: 'defence',
      domain: 'drone',
      cost: { fuel: 25 },
      producer: 'drone_bay',
      // flown over a radio link: an EMP drops it
      electronic: true,
    },
    attack_heli: {
      name: 'Attack Helicopter',
      real: 'Ka-52 Alligator (30 mm 2A42 cannon)',
      role: 'Hovers over the fight and rakes the guns with ten-round cannon bursts; side-slips and drops flares when fired on.',
      hp: 180,
      // ten-round bursts from the cannon
      damage: 4,
      burst: 10,
      burstGap: 1,
      attackTicks: 30,
      range: Math.floor(TILE * 3),
      speed: tilesPerSecond(2.5),
      housing: 4,
      prefers: 'defence',
      domain: 'aircraft',
      cost: { fuel: 300 },
      tier: 3,
      producer: 'airfield',
      tech: 'flight',
      // fired on, it jinks and drops flares
      evasion: 0.25,
    },
    attack_drone: {
      name: 'Loitering Munition',
      real: 'ZALA Lancet-3',
      role: 'A one-way precision drone: it picks a defence and dives onto it with a three-kilo warhead.',
      // a one-way drone: it dives onto its target and is gone
      oneWay: true,
      hp: 40,
      damage: 120,
      // a three-kilo warhead: the blast reaches half a tile
      splash: Math.floor(TILE * 0.5),
      attackTicks: 1,
      range: Math.floor(TILE * 0.5),
      speed: tilesPerSecond(3),
      housing: 1,
      prefers: 'defence',
      domain: 'drone',
      cost: { fuel: 40 },
      tier: 2,
      electronic: true,
      producer: 'drone_bay',
    },
    fpv_drone: {
      name: 'FPV Strike Drone',
      real: 'FPV quadcopter (PG-7VL shaped charge)',
      role: 'A racing quadcopter with an RPG warhead strapped on: the fastest, cheapest thing in the air, flown straight into armour.',
      oneWay: true,
      hp: 10,
      damage: 90,
      antiArmor: 2,
      // a shaped charge: a narrow blast
      splash: Math.floor(TILE * 0.4),
      attackTicks: 1,
      range: Math.floor(TILE * 0.4),
      speed: tilesPerSecond(5),
      housing: 1,
      prefers: 'defence',
      domain: 'drone',
      cost: { fuel: 15 },
      producer: 'drone_bay',
      electronic: true,
    },
    shahed: {
      name: 'One-Way Attack Drone',
      real: 'Geran-2 (Shahed-136)',
      role: "Slow, loud and cheap for what it carries: a fifty-kilo warhead flown into the base's stores and collectors, not its guns.",
      // slow and loud, with a 50 kg warhead: it goes for the base's infrastructure, not its guns
      oneWay: true,
      hp: 60,
      damage: 250,
      splash: Math.floor(TILE * 1.8),
      attackTicks: 1,
      range: Math.floor(TILE * 0.5),
      speed: tilesPerSecond(2.5),
      housing: 2,
      prefers: 'resource',
      domain: 'drone',
      cost: { fuel: 80, steel: 40 },
      tier: 3,
      producer: 'drone_bay',
      electronic: true,
    },
    strike_jet: {
      name: 'Carrier Strike Fighter',
      real: 'J-15 Flying Shark (KD-88 missiles, 30 mm cannon)',
      role: 'Off a carrier offshore: blitz passes with a pair of missiles from six tiles out, cannon on the way over, then out and round again.',
      // launched off an aircraft carrier, it comes in low and very fast on a blitz pass: a pair of air-to-ground
      // missiles from six tiles out, then its 30 mm cannon strafing the target on the way over, then out past the
      // edge of the map and round for another run. Fired on, it breaks hard and drops flares.
      jet: true,
      launchedFrom: 'carrier',
      evasion: 0.4,
      hp: 260,
      // each of the pair of KD-88 missiles
      damage: 220,
      splash: Math.floor(TILE * 1.5),
      attackTicks: 8 * TICKS_PER_SECOND,
      range: TILE * 6,
      gun: { damage: 18, everyTicks: 2, range: TILE * 3 },
      speed: tilesPerSecond(20),
      housing: 8,
      prefers: 'defence',
      domain: 'aircraft',
      cost: { fuel: 900, steel: 300 },
      tier: 4,
      producer: 'airfield',
      tech: 'flight',
    },
    // the defender's fighter, kept on patrol by an air wing; never trained by the attacker
    f16: {
      name: 'Fighter',
      real: 'F-16C Block 70 (AIM-9X, M61 20 mm)',
      role: "The defender's air patrol: flies off a carrier offshore and circles the base very fast, shooting down whatever flies.",
      // it flies off a carrier offshore and patrols over the base very fast
      jet: true,
      hp: 300,
      damage: 60,
      attackTicks: 30,
      range: TILE * 8,
      speed: tilesPerSecond(16),
      housing: 0,
      prefers: 'any',
      domain: 'aircraft',
    },
    // Strikes: fielded like units (trained, housed, deployed by tile), but they land once.
    nuke: {
      name: 'Nuke',
      real: 'Thermonuclear warhead (Bulava SLBM)',
      role: 'A city-killer on a submarine-launched missile: seven tiles levelled and a shockwave beyond. THAAD and Arrow 3 can stop the missile.',
      hp: 1,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 6,
      prefers: 'any',
      cost: { fuel: 1200, steel: 800 },
      tier: 3,
      // a city-killer: seven tiles of devastation that levels everything in it, and a shockwave beyond
      strike: { kind: 'nuke', radius: TILE * 7, damage: 6000, edgeDamage: 2500, cooldownTicks: 90 * TICKS_PER_SECOND },
      // a submarine-launched ballistic missile: it breaks the surface from a submarine on the sea and arcs
      // in over five seconds, and THAAD and Arrow 3 can shoot it down on the way
      deliveredBy: 'icbm',
      launchedFrom: 'submarine',
    },
    emp: {
      name: 'EMP',
      real: 'High-power microwave warhead (CHAMP-type)',
      role: 'A microwave burst from a cruise missile fired by a frigate: every radar, launcher and drone within four tiles goes dark for five seconds.',
      hp: 1,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 3,
      prefers: 'any',
      cost: { fuel: 300, steel: 300 },
      tier: 2,
      strike: { kind: 'emp', radius: TILE * 4, disableTicks: 5 * TICKS_PER_SECOND, cooldownTicks: 40 * TICKS_PER_SECOND },
      // a high-power microwave cruise missile fired from a frigate: it flies in low and can be shot down
      deliveredBy: 'emp_missile',
      launchedFrom: 'destroyer',
    },
    emp_missile: {
      name: 'EMP Missile',
      real: 'CHAMP-type high-power microwave cruise missile',
      role: 'The low, slow cruise missile that carries the EMP in from a frigate; Iron Dome and C-RAM can shoot it down.',
      threat: 'cruise',
      domain: 'missile',
      hp: 30,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 0,
      prefers: 'any',
      warhead: { fuseTicks: 5 * TICKS_PER_SECOND + 1, radius: 0, damage: 0, edgeDamage: 0, cooldownTicks: 0 },
    },
    ballistic: {
      name: 'Ballistic Missile',
      real: '9K720 Iskander-M',
      role: 'A quasi-ballistic missile: five seconds of terminal dive for Patriot or THAAD to stop it, then a half-tonne warhead.',
      // a quasi-ballistic missile: three seconds of terminal dive, which Patriot, David's Sling, THAAD and Arrow can engage
      threat: 'ballistic',
      domain: 'missile',
      hp: 30,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 3,
      prefers: 'any',
      cost: { fuel: 400, steel: 200 },
      tier: 3,
      warhead: { fuseTicks: 5 * TICKS_PER_SECOND, radius: Math.floor(TILE * 1.5), damage: 500, edgeDamage: 250, cooldownTicks: 30 * TICKS_PER_SECOND },
    },
    hgv: {
      name: 'Hypersonic Glide Vehicle',
      real: 'DF-17 (DF-ZF glide vehicle)',
      role: 'Glides in above Mach 5, manoeuvring all the way: four seconds of warning, and only THAAD has a chance.',
      // it glides in at Mach 5 and above, manoeuvring all the way: a second and a half of warning, and only THAAD or Arrow 3 have a chance
      threat: 'hgv',
      domain: 'missile',
      hp: 30,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 4,
      prefers: 'any',
      cost: { fuel: 700, steel: 400 },
      tier: 4,
      warhead: { fuseTicks: 4 * TICKS_PER_SECOND, radius: Math.floor(TILE * 1.5), damage: 600, edgeDamage: 300, cooldownTicks: 60 * TICKS_PER_SECOND },
    },
    // the nuclear missile: fired from a submarine offshore at DEFCON 1, it breaks the surface and arcs in with a
    // thermonuclear warhead; THAAD is the one thing that reaches it. In a raid it was the vehicle the nuke strike rode in on.
    icbm: {
      name: 'Nuclear ICBM',
      real: 'RSM-56 Bulava SLBM, thermonuclear warhead',
      role: 'Off a submarine offshore: breaks the surface and arcs in over five seconds, and seven tiles are levelled where it comes down. Only THAAD can reach it.',
      threat: 'icbm',
      domain: 'missile',
      hp: 60,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 0,
      prefers: 'any',
      // a city-killer: seven tiles of devastation that levels everything in it, and a shockwave beyond
      warhead: { fuseTicks: 5 * TICKS_PER_SECOND, radius: TILE * 7, damage: 6000, edgeDamage: 2500, cooldownTicks: 0 },
    },
    // the nuclear glide vehicle: the hypersonic above with a warhead to match, DEFCON 1 only
    hgv_nuclear: {
      name: 'Nuclear Glide Vehicle',
      real: 'Avangard (hypersonic glide vehicle, nuclear)',
      role: 'A hypersonic glide vehicle with a nuclear warhead: four seconds of warning, five tiles levelled, and only THAAD has a chance.',
      threat: 'hgv',
      domain: 'missile',
      hp: 30,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 5,
      prefers: 'any',
      warhead: { fuseTicks: 4 * TICKS_PER_SECOND, radius: TILE * 5, damage: 4000, edgeDamage: 1500, cooldownTicks: 0 },
    },
    cruise_missile: {
      name: 'Cruise Missile',
      real: '9M728 Iskander-K (ground-launched)',
      role: 'A ground-launched cruise missile from beyond the map: flies low at the nearest building, locked on, with a half-tonne warhead.',
      // locked on at launch: if its target falls first it flies on to where the target was and is wasted
      threat: 'cruise',
      oneWay: true,
      hp: 30,
      // a 480 kg warhead
      damage: 130,
      splash: Math.floor(TILE * 1.5),
      attackTicks: 1,
      range: Math.floor(TILE * 0.5),
      speed: tilesPerSecond(6),
      housing: 2,
      prefers: 'any',
      domain: 'missile',
      cost: { fuel: 200, steel: 100 },
      tier: 3,
    },
    // the frigate's missile: never trained, it is fired from the ship's launchers (see destroyer.launches)
    kalibr: {
      name: 'Ship-Launched Cruise Missile',
      real: '3M-14 Kalibr',
      role: "Fired from a frigate's vertical launchers every six seconds once it is in range: the same low flight, from the sea.",
      threat: 'cruise',
      oneWay: true,
      hp: 30,
      // a 450 kg warhead
      damage: 120,
      splash: Math.floor(TILE * 1.5),
      attackTicks: 1,
      range: Math.floor(TILE * 0.5),
      speed: tilesPerSecond(6),
      housing: 2,
      prefers: 'any',
      domain: 'missile',
    },
    // Ships: trained at a Shipyard, deployed onto the open sea and moved over water only.
    boat: {
      name: 'Patrol Boat',
      real: 'Raptor class (14.5 mm KPVT)',
      role: 'Fast and light: works the shore shelling the plan with a heavy machine gun, and its point defence downs a quarter of the missiles fired at it.',
      hp: 70,
      // five-round bursts from the heavy machine gun
      damage: 3,
      burst: 5,
      burstGap: 2,
      attackTicks: 20,
      range: TILE * 4,
      speed: tilesPerSecond(3),
      housing: 2,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 120 },
      tier: 2,
      producer: 'shipyard',
      // a patrol boat's light point defence downs a quarter of the missiles fired at it
      interceptChance: 0.25,
    },
    destroyer: {
      name: 'Frigate',
      real: 'Admiral Gorshkov class',
      role: 'Closes to twenty tiles and fires a Kalibr every six seconds, then shells the shore with its 130 mm gun; its point defence downs half the missiles fired at it.',
      hp: 320,
      // the 130 mm deck gun, a round every two seconds
      damage: 40,
      splash: Math.floor(TILE * 0.8),
      attackTicks: 40,
      range: TILE * 6,
      speed: tilesPerSecond(2),
      housing: 6,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 500, steel: 300 },
      tier: 3,
      producer: 'shipyard',
      // it fires a Kalibr from its vertical launchers every six seconds, and its point defence shoots down half the missiles fired at it
      // it has to close to within twenty tiles before it starts firing them
      launches: { unit: 'kalibr', everyTicks: 6 * TICKS_PER_SECOND, range: TILE * 20 },
      interceptChance: 0.5,
    },
    // the fleet's drone ship: it holds station far offshore and launches one-way attack drones at the Command Center
    drone_ship: {
      name: 'Drone Carrier',
      real: 'Shahid Mahdavi class (container ship with a drone deck)',
      role: 'A converted container ship that holds station far offshore and launches one-way attack drones at the Command Center, out of reach of every gun.',
      hp: 900,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: tilesPerSecond(2),
      housing: 10,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 600, steel: 400 },
      tier: 3,
      producer: 'shipyard',
    },
    landing_craft: {
      name: 'Landing Craft',
      real: 'Zubr class air-cushion landing craft',
      role: 'Rides up to the beach at sixty knots, puts a rifle platoon and an anti-tank team ashore, then fights on with its gatling guns.',
      // it rides up to the beach and puts a rifle platoon and an anti-tank team ashore, then fights on with its AK-630s
      hp: 300,
      damage: 3,
      burst: 10,
      burstGap: 1,
      attackTicks: 30,
      range: TILE * 3,
      speed: tilesPerSecond(4),
      housing: 12,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 450, steel: 200 },
      tier: 3,
      producer: 'shipyard',
      carries: { units: { rifleman: 6, at_team: 1 }, afterTicks: 3 * TICKS_PER_SECOND },
    },
    carrier: {
      name: 'Aircraft Carrier',
      real: 'Type 003 Fujian (catapult carrier)',
      role: 'Holds station offshore and launches the strike fighters; its close-in guns down half the missiles fired at it.',
      // it holds station off the coast and launches the strike fighters; its close-in guns down some missiles fired at it
      hp: 1800,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 12,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 1500, steel: 1200 },
      tier: 4,
      producer: 'shipyard',
      interceptChance: 0.5,
    },
    submarine: {
      name: 'Missile Submarine',
      real: 'Borei class SSBN',
      role: 'Lies submerged offshore, out of reach of every defence, and fires nothing itself: the launch platform every nuke needs.',
      // it lies submerged off the coast, out of reach of every defence the base has, and fires nothing
      // itself: it is the launch platform every nuke needs
      threat: 'submarine',
      hp: 600,
      damage: 0,
      attackTicks: 1,
      range: 0,
      speed: 0,
      housing: 8,
      prefers: 'any',
      domain: 'sea',
      cost: { fuel: 900, steel: 700 },
      tier: 3,
      producer: 'shipyard',
    },
    conscript: {
      name: 'Conscript',
      real: 'AKM rifle, no body armour',
      role: 'Militia: ten come free with every raid. Worse armed and worse protected than a rifleman, but there are always more.',
      infantry: true,
      hp: 30,
      damage: 2,
      burst: 3,
      burstGap: 2,
      attackTicks: 20,
      range: Math.floor(TILE * 0.6),
      speed: tilesPerSecond(2),
      housing: 1,
      prefers: 'any',
    },
  },
  buildings: {
    hq: {
      name: 'Command Center', hp: 450, size: 4, kind: 'hq',
      cost: { fuel: 600, steel: 600 },
      stores: { fuel: 1000, steel: 1000, supplies: 500 },
      housing: 10,
      maxCount: [1],
    },
    oil_derrick: {
      name: 'Oil Derrick', hp: 200, size: 3, kind: 'resource',
      cost: { steel: 150 }, produces: { resource: 'fuel', perHour: 200 }, maxCount: [2, 3, 4, 5, 6],
    },
    steel_mill: {
      name: 'Steel Mill', hp: 200, size: 3, kind: 'resource',
      cost: { fuel: 150 }, produces: { resource: 'steel', perHour: 200 }, maxCount: [2, 3, 4, 5, 6],
    },
    // Power: clean generators that feed Fuel. Nuclear is locked until HQ 3.
    solar_array: {
      name: 'Solar Array', hp: 150, size: 2, kind: 'resource',
      cost: { steel: 120 }, produces: { resource: 'fuel', perHour: 120 }, maxCount: [2, 3, 4, 5, 6],
    },
    wind_turbine: {
      name: 'Wind Turbine', hp: 180, size: 2, kind: 'resource',
      cost: { steel: 150 }, produces: { resource: 'fuel', perHour: 160 }, maxCount: [1, 2, 2, 3, 4],
    },
    nuclear_reactor: {
      name: 'Nuclear Reactor', hp: 400, size: 4, kind: 'resource',
      cost: { fuel: 500, steel: 500 }, produces: { resource: 'fuel', perHour: 600 }, maxCount: [0, 0, 1, 1, 2],
    },
    supply_depot: {
      name: 'Supply Depot', hp: 180, size: 2, kind: 'resource',
      cost: { fuel: 100, steel: 100 }, produces: { resource: 'supplies', perHour: 60 }, maxCount: [1, 1, 2, 2, 3],
    },
    // Nets and boats out on the sea past the shore. Placed like any resource
    // building inside the plan; the catch is taken to it from the water.
    fishery: {
      name: 'Fishery', hp: 220, size: 3, kind: 'resource',
      cost: { fuel: 150, steel: 100 }, produces: { resource: 'supplies', perHour: 80 }, maxCount: [1, 1, 2, 2, 3],
    },
    fuel_tank: {
      name: 'Fuel Tank', hp: 300, size: 3, kind: 'storage',
      cost: { steel: 300 }, stores: { fuel: 2000 }, maxCount: [1, 2, 2, 3, 3],
    },
    steel_yard: {
      name: 'Steel Yard', hp: 300, size: 3, kind: 'storage',
      cost: { fuel: 300 }, stores: { steel: 2000 }, maxCount: [1, 2, 2, 3, 3],
    },
    warehouse: {
      name: 'Warehouse', hp: 300, size: 3, kind: 'storage',
      cost: { fuel: 200, steel: 200 }, stores: { supplies: 1000 }, maxCount: [1, 1, 1, 2, 2],
    },
    staging_area: {
      name: 'Staging Area', hp: 250, size: 3, kind: 'army',
      cost: { fuel: 250 }, housing: 10, maxCount: [1, 2, 2, 3, 3],
    },
    barrier: {
      name: 'Concrete Barrier', hp: 300, size: 1, kind: 'wall',
      cost: { steel: 20 }, maxCount: [40, 60, 80, 100, 120],
    },
    farm: {
      name: 'Farm', hp: 200, size: 3, kind: 'resource',
      cost: { fuel: 120, steel: 120 }, produces: { resource: 'supplies', perHour: 50 }, needsWater: true, maxCount: [1, 2, 2, 3, 4],
    },
    water_supply: {
      name: 'Water Supply', hp: 220, size: 2, kind: 'resource',
      cost: { steel: 200 }, waters: 3, maxCount: [1, 1, 2, 2, 3],
    },
    barracks: {
      name: 'Barracks', hp: 300, size: 3, kind: 'production',
      cost: { steel: 200 }, maxCount: [1, 1, 2, 2, 3],
    },
    tank_factory: {
      name: 'Tank Factory', hp: 350, size: 3, kind: 'production',
      cost: { fuel: 400, steel: 300 }, maxCount: [0, 1, 1, 2, 2],
    },
    airfield: {
      name: 'Airfield', hp: 350, size: 3, kind: 'production',
      cost: { fuel: 500, steel: 400 }, maxCount: [0, 0, 1, 1, 2], tech: 'flight',
    },
    drone_bay: {
      name: 'Drone Bay', hp: 220, size: 2, kind: 'production',
      cost: { fuel: 200, steel: 300 }, maxCount: [1, 1, 1, 2, 2], tech: 'drones',
    },
    research_lab: {
      name: 'Research Lab', hp: 250, size: 3, kind: 'research',
      cost: { fuel: 300, steel: 300 }, maxCount: [1, 1, 1, 1, 1],
    },
    // ---- defences, each modelled on a real system ----
    // Centurion C-RAM: a land-based Phalanx, an M61A1 20 mm gatling firing 4,500 rounds a minute
    // (75 a second) of self-destructing HEIT rounds at rockets, mortar rounds, drones, cruise missiles
    // and low helicopters. Each sim tick is a volley of four rounds. It fires bursts of up to a second
    // and a half, ceases fire the moment its target is down, takes half a second to slew onto the next,
    // and carries the real 1,550-round drum, with no reload in a raid.
    c_ram: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'C-RAM', real: 'Centurion C-RAM (M61A1 20 mm, 4,500 rpm)', hp: 340, size: 2, kind: 'defence',
      damage: 40, burst: 30, burstGap: 1, attackTicks: Math.floor(TICKS_PER_SECOND / 2), range: TILE * 20,
      ammo: Math.floor(1550 / 4), ceaseOnKill: true,
      // HEIT-SD rounds burst in the air: a swarm of drones close together goes down together
      splash: Math.floor(TILE * 0.4),
      // per four-round volley; a full burst nearly always gets a cruise missile (about 96 percent).
      // It can depress onto troops and vehicles too, but the air threats always come first.
      interceptChance: { rocket: 0.15, cruise: 0.1, drone: 0.5, aircraft: 0.08, infantry: 0.12, vehicle: 0.1 },
      targets: ['rocket', 'cruise', 'drone', 'aircraft', 'infantry', 'vehicle'],
      defaultPriority: ['cruise', 'rocket', 'drone', 'aircraft'],
      cost: { fuel: 300, steel: 300 }, maxCount: [0, 1, 1, 2, 2],
    },
    // Naval Strike Missile coastal battery: four sea-skimming missiles in a salvo, then a long reload; ships may shoot them down
    missile_battery: {
      name: 'NSM Coastal Battery', real: 'Kongsberg NSM Coastal Defence System', hp: 300, size: 2, kind: 'defence', missiles: true,
      damage: 160, burst: 4, burstGap: 10, attackTicks: 30 * TICKS_PER_SECOND, range: TILE * MAP_SIZE, ammo: 20,
      projectile: tilesPerSecond(8),
      splash: TILE,
      targets: ['ship'], cost: { fuel: 400, steel: 300 }, maxCount: [0, 1, 1, 2, 2],
    },
    // K-300P Bastion-P: two P-800 Oniks, Mach 2.5 and very heavy, then a very long reload
    bastion: {
      name: 'Bastion-P', real: 'K-300P Bastion-P (P-800 Oniks)', hp: 380, size: 3, kind: 'defence', missiles: true,
      damage: 350, burst: 2, burstGap: 20, attackTicks: 45 * TICKS_PER_SECOND, range: TILE * MAP_SIZE, ammo: 8,
      projectile: tilesPerSecond(16),
      splash: Math.floor(TILE * 1.5),
      targets: ['ship'], cost: { fuel: 700, steel: 500 }, maxCount: [0, 0, 0, 1, 1],
    },
    // Barrett M82A1: a .50 calibre anti-materiel rifle, an aimed shot every three seconds
    sniper_nest: {
      name: 'Sniper Nest', real: 'Barrett M82A1', hp: 260, size: 2, kind: 'defence',
      damage: 150, attackTicks: 3 * TICKS_PER_SECOND, range: TILE * MAP_SIZE * 2, ammo: 25,
      // snipers pick off men; armour is the ATGM post's job
      targets: ['infantry'],
      cost: { steel: 300 }, maxCount: [2, 3, 4, 5, 6],
      manned: true,
    },
    // FGM-148 Javelin team: fire-and-forget, top attack, one missile and a long reload
    atgm_post: {
      name: 'ATGM Post', real: 'FGM-148 Javelin', hp: 260, size: 2, kind: 'defence', missiles: true,
      damage: 300, attackTicks: 10 * TICKS_PER_SECOND, range: TILE * 10, ammo: 12,
      projectile: tilesPerSecond(6),
      splash: Math.floor(TILE * 0.6),
      targets: ['vehicle', 'ship'], cost: { fuel: 200, steel: 250 }, maxCount: [0, 1, 2, 2, 3],
      manned: true,
    },
    // M777A2 155 mm lightweight towed howitzer in a sandbagged emplacement: high-explosive shells that
    // arc out to fourteen tiles and blast every ground unit within two and a bit tiles of impact
    howitzer: {
      name: 'Howitzer', real: 'M777A2 155 mm howitzer (M795 HE)', hp: 340, size: 3, kind: 'defence',
      damage: 110, attackTicks: 5 * TICKS_PER_SECOND, range: TILE * 14, minRange: TILE * 3, ammo: 30,
      splash: Math.floor(TILE * 2.2),
      targets: ['infantry', 'vehicle'], cost: { fuel: 250, steel: 450 }, maxCount: [0, 1, 2, 2, 3],
      manned: true,
    },
    tank_depot: {
      name: 'Tank Depot', hp: 400, size: 3, kind: 'defence',
      cost: { fuel: 300, steel: 400 }, maxCount: [0, 1, 1, 2, 2],
      patrol: { unit: 'tank', radius: TILE * 6 },
    },
    helipad: {
      name: 'Helipad', hp: 300, size: 2, kind: 'defence', tech: 'flight',
      cost: { fuel: 350, steel: 250 }, maxCount: [0, 1, 1, 2, 2],
      patrol: { unit: 'attack_heli', radius: TILE * 9 },
    },
    // the command post ashore for a carrier air wing: its fighter flies off a carrier standing offshore and patrols
    // over a wide circle round the base
    airbase: {
      name: 'Carrier Air Wing', real: 'Air wing command post (F-16s off a carrier offshore)', hp: 600, size: 4, kind: 'defence', tech: 'flight',
      cost: { fuel: 900, steel: 700 }, maxCount: [0, 0, 0, 1, 1],
      patrol: { unit: 'f16', radius: TILE * 18, fromSea: true },
    },
    shipyard: {
      name: 'Shipyard', hp: 350, size: 3, kind: 'production',
      cost: { fuel: 400, steel: 300 }, maxCount: [0, 1, 1, 2, 2],
    },
    // MK 67 submarine-launched mobile mine / moored contact mine
    sea_mine: {
      name: 'Sea Mine', real: 'Moored contact mine', hp: 40, size: 1, kind: 'defence', sea: true,
      damage: 120, attackTicks: 1, range: Math.floor(TILE * 0.75), mine: { splash: Math.floor(TILE * 1.5) },
      targets: ['ship'], cost: { steel: 80 }, maxCount: [2, 3, 4, 5, 6],
    },
    fishing_boat: {
      name: 'Fishing Boat', hp: 120, size: 1, kind: 'resource', sea: true, producer: 'shipyard',
      cost: { fuel: 60, steel: 40 }, produces: { resource: 'supplies', perHour: 40 }, maxCount: [4, 6, 8, 10, 12],
    },
    // a concrete pillbox with a four-man squad (M4 carbines and an M249) firing out of its slits;
    // rifles and drones barely mark it, but anti-tank rockets, tank rounds and charges break it open
    bunker: {
      name: 'Bunker', real: 'Concrete pillbox, 4-man squad (M4 / M249)', hp: 700, size: 2, kind: 'defence',
      damage: 4, burst: 3, burstGap: 2, attackTicks: 20, range: TILE * 6,
      garrison: 4, fortified: true,
      targets: ['infantry', 'vehicle'], cost: { steel: 500, fuel: 150 }, maxCount: [1, 2, 2, 3, 4],
    },
    // M2 Browning .50 cal: 450 to 600 rounds a minute, fired in bursts of about six
    mg_nest: {
      name: 'Machine Gun Nest',
      real: 'M2 Browning .50 cal',
      hp: 380,
      size: 3,
      kind: 'defence',
      damage: 10,
      burst: 6,
      burstGap: 2,
      attackTicks: 30,
      range: TILE * 7,
      cost: { steel: 400 },
      maxCount: [2, 3, 4, 5, 6],
      electronic: true,
      targets: ['infantry', 'vehicle', 'ship'],
      manned: true,
    },
    electric_fence: {
      name: 'Electric Fence',
      hp: 220,
      size: 1,
      kind: 'wall',
      damage: 5,
      attackTicks: 10,
      range: Math.floor(TILE * 0.75),
      shock: true,
      targets: ['infantry', 'vehicle'],
      cost: { steel: 30 },
      maxCount: [40, 60, 80, 100, 120],
    },
    // PMN-2 anti-personnel blast mine: a foot on it sets it off; a tank rolls over it
    land_mine: {
      name: 'Anti-Personnel Mine',
      real: 'PMN-2',
      hp: 40,
      size: 1,
      kind: 'defence',
      damage: 90,
      attackTicks: 1,
      range: Math.floor(TILE * 0.75),
      mine: { splash: Math.floor(TILE * 1.5) },
      targets: ['infantry'],
      cost: { steel: 60 },
      maxCount: [4, 6, 8, 10, 12],
    },
    // TM-62M anti-tank mine: only the weight of a vehicle sets it off, and it guts a tank
    at_mine: {
      name: 'Anti-Tank Mine',
      real: 'TM-62M',
      hp: 60,
      size: 1,
      kind: 'defence',
      damage: 400,
      attackTicks: 1,
      range: Math.floor(TILE * 0.75),
      mine: { splash: Math.floor(TILE * 1.5) },
      targets: ['vehicle'],
      cost: { steel: 120 },
      maxCount: [0, 2, 4, 6, 8],
    },
    // Pantsir-S1: twin 30 mm cannons and 57E6 missiles against aircraft and drones
    air_defence: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'Air Defence',
      real: 'Pantsir-S1',
      hp: 350,
      size: 2,
      kind: 'defence',
      damage: 11,
      burst: 8,
      burstGap: 2,
      attackTicks: 20,
      range: TILE * 9,
      targets: ['aircraft', 'drone'],
      splash: Math.floor(TILE * 0.5),
      cost: { steel: 450, fuel: 200 },
      maxCount: [1, 2, 2, 3, 3],
    },
    // The missile defence ladder is three rungs, each reaching higher than the last: Iron Dome for rockets and
    // cruise missiles, Patriot for ballistic missiles and aircraft, THAAD for the ICBM and the glide vehicle.
    // Iron Dome: a Tamir interceptor for each rocket or cruise missile, twenty tubes, then a long reload
    missile_defence: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'Iron Dome',
      real: 'Iron Dome (Tamir interceptor)',
      hp: 420,
      size: 3,
      kind: 'defence',
      damage: 90,
      burst: 20,
      burstGap: 2 * TICKS_PER_SECOND,
      attackTicks: 60 * TICKS_PER_SECOND,
      projectile: tilesPerSecond(14),
      salvo: 1,
      range: TILE * 14,
      targets: ['rocket', 'cruise'],
      cost: { steel: 500, fuel: 700 },
      maxCount: [1, 1, 2, 2, 3],
      ammo: 60,
      interceptChance: { rocket: 0.9, cruise: 0.8 },
    },
    // Patriot PAC-3 MSE: a ripple of two at each ballistic or cruise missile, sixteen to a launcher
    patriot: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'Patriot', real: 'Patriot PAC-3 MSE', hp: 450, size: 3, kind: 'defence',
      damage: 90, burst: 16, burstGap: 1 * TICKS_PER_SECOND, attackTicks: 60 * TICKS_PER_SECOND,
      projectile: tilesPerSecond(18), salvo: 2, range: TILE * 18, ammo: 32,
      // and, as the real system was first built for, aircraft
      targets: ['cruise', 'ballistic', 'aircraft'], interceptChance: { cruise: 0.75, ballistic: 0.7, aircraft: 0.6 },
      defaultPriority: ['ballistic', 'cruise', 'aircraft'],
      cost: { steel: 700, fuel: 800 }, maxCount: [0, 0, 1, 2, 3],
    },
    // THAAD: hit-to-kill high in the terminal phase, the top of the ladder: the one that can reach an ICBM, and a glide vehicle on a good day
    thaad: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'THAAD', real: 'Terminal High Altitude Area Defense', hp: 450, size: 3, kind: 'defence',
      damage: 120, burst: 8, burstGap: 2 * TICKS_PER_SECOND, attackTicks: 90 * TICKS_PER_SECOND,
      projectile: tilesPerSecond(24), salvo: 2, range: TILE * MAP_SIZE, ammo: 16,
      targets: ['ballistic', 'icbm', 'hgv'], interceptChance: { ballistic: 0.85, icbm: 0.6, hgv: 0.45 },
      cost: { steel: 900, fuel: 1100 }, maxCount: [0, 0, 0, 1, 2],
    },
    // Rheinmetall Skynex: 35 mm revolver guns firing AHEAD airburst rounds at 1,000 a minute; drones only
    drone_interceptor: {
      // radar-guided: an EMP knocks it out
      electronic: true,
      name: 'Drone Interceptor',
      real: 'Rheinmetall Skynex (35 mm AHEAD)',
      hp: 200,
      size: 2,
      kind: 'defence',
      damage: 7,
      burst: 12,
      burstGap: 1,
      attackTicks: 20,
      range: TILE * 6,
      targets: ['drone'],
      // AHEAD rounds burst into a cloud of tungsten pellets in front of the drone
      splash: Math.floor(TILE * 0.8),
      cost: { steel: 200, fuel: 100 },
      maxCount: [2, 3, 4, 5, 6],
    },
  },
};
